import { FormEvent, useEffect, useId, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { api } from "../../api/client";
import { AlertBanner } from "../../components/feedback/AlertBanner";
import { EarTag } from "../../components/tags/EarTag";
import { TagsSubnav } from '../../components/subnav/TagsSubnav';
import { ResponsiveTable } from '../../components/ui/ResponsiveTable';
import { apiError } from "../../lib/apiError";
import { formatDateTime } from "../../lib/format";
import { passwordFieldProps } from "../../lib/inputPrivacy";
import { useModalHistoryEntry } from "../../lib/modalHistory";
import type { TagVerificationSessionDetail, TagVerificationSummary } from "../../types";

export function TagHistoryPage() {
  const fieldNs = useId().replace(/:/g, "");
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState<TagVerificationSummary[]>([]);
  const [detail, setDetail] = useState<TagVerificationSessionDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [password, setPassword] = useState("");

  const selectedId = Number(searchParams.get("session") || "") || null;

  async function loadList() {
    const result = await api.GET("/api/tag-verifications");
    if (result.error) {
      setError(apiError(result.error, "Nie udało się wczytać historii."));
      return;
    }
    setError(null);
    setItems((result.data as TagVerificationSummary[]) ?? []);
  }

  async function loadDetail(sessionId: number) {
    setBusy(true);
    const result = await api.GET("/api/tag-verifications/{session_id}", {
      params: { path: { session_id: sessionId } },
    });
    setBusy(false);
    if (result.error) {
      setError(apiError(result.error, "Nie udało się wczytać szczegółów sesji."));
      setDetail(null);
      return;
    }
    setError(null);
    setDetail(result.data as TagVerificationSessionDetail);
  }

  useEffect(() => {
    void loadList();
  }, []);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      return;
    }
    void loadDetail(selectedId);
  }, [selectedId]);

  useModalHistoryEntry(deleteOpen, () => {
    setDeleteOpen(false);
    setPassword("");
  });

  const listEntries = useMemo(
    () => (detail?.entries ?? []).filter((entry) => entry.found_in_list),
    [detail],
  );
  const notFoundEntries = useMemo(
    () => (detail?.entries ?? []).filter((entry) => !entry.found_in_list),
    [detail],
  );

  async function onDelete(event: FormEvent) {
    event.preventDefault();
    if (!selectedId || !password) {
      setError("Podaj hasło administratora.");
      return;
    }
    setBusy(true);
    const deleted = await api.DELETE("/api/tag-verifications/{session_id}", {
      params: { path: { session_id: selectedId } },
      body: { password },
    });
    setBusy(false);
    if (deleted.error) {
      setError(apiError(deleted.error, "Nie udało się usunąć sesji."));
      return;
    }
    setDeleteOpen(false);
    setPassword("");
    setDetail(null);
    setSearchParams({});
    await loadList();
  }

  return (
    <main className="page" data-testid="tags-history-page">
      <header className="page__header page__header--row">
        <div>
          <p className="page__kicker">Kontrole</p>
          <h1>Historia kontroli</h1>
        </div>
        <Link className="btn btn--ghost" to="/controls">
          Nowa sesja
        </Link>
      </header>
      <TagsSubnav />

      <AlertBanner variant="error" message={error} onDismiss={() => setError(null)} />

      <section className="panel">
        <h2 className="panel__title">Sesje</h2>
        {items.length === 0 ? (
          <p className="muted">Brak zakończonych sesji.</p>
        ) : (
          <div className="table-wrap">
            <ResponsiveTable className="tbl" data-testid="tags-history-table">
              <thead>
                <tr>
                  <th>Tytuł</th>
                  <th>Rodzaj</th>
                  <th>Data</th>
                  <th>Podsumowanie</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr
                    key={item.id}
                    className={selectedId === item.id ? "tbl__row--active" : undefined}
                  >
                    <td>
                      <button
                        type="button"
                        className="btn--link"
                        data-testid="tags-history-select"
                        onClick={() => setSearchParams({ session: String(item.id) })}
                      >
                        {item.title}
                      </button>
                    </td>
                    <td>Weryfikacja kolczyków</td>
                    <td>{formatDateTime(item.completed_at)}</td>
                    <td>
                      {item.verified_count} / {item.total_count} zweryfikowanych ·{" "}
                      {item.not_found_count} niezarejestrowanych
                    </td>
                  </tr>
                ))}
              </tbody>
            </ResponsiveTable>
          </div>
        )}
      </section>

      {selectedId ? (
        <section className="panel" data-testid="tags-history-detail">
          {busy && !detail ? <p className="muted">Wczytywanie szczegółów…</p> : null}
          {detail ? (
            <>
              <div className="panel__header">
                <div>
                  <h2 className="panel__title">{detail.title}</h2>
                  <p className="muted">{formatDateTime(detail.completed_at)}</p>
                  {detail.description ? <p className="muted">{detail.description}</p> : null}
                </div>
                <div className="panel__header-actions">
                  <button
                    type="button"
                    className="btn btn--danger btn--sm"
                    data-testid="tags-history-delete"
                    onClick={() => {
                      setPassword("");
                      setDeleteOpen(true);
                    }}
                  >
                    Usuń sesję
                  </button>
                </div>
              </div>
              <p className="muted" data-testid="tags-history-stats">
                Na liście: {detail.total_count} · Zweryfikowany: {detail.verified_count} ·
                Niezarejestrowane: {detail.not_found_count}
              </p>

              <h3 className="panel__title">Kolczyki z listy</h3>
              <div className="table-wrap">
                <ResponsiveTable className="tbl">
                  <thead>
                    <tr>
                      <th>Kolczyk</th>
                      <th>Status</th>
                      <th>Liczba</th>
                      <th>Czas</th>
                    </tr>
                  </thead>
                  <tbody>
                    {listEntries.length === 0 ? (
                      <tr className="tbl__empty-row">
                        <td colSpan={4} className="muted">
                          Brak wpisów.
                        </td>
                      </tr>
                    ) : (
                      listEntries.map((entry) => (
                        <tr key={entry.id}>
                          <td>
                            <EarTag value={entry.tag} />
                          </td>
                          <td>{entry.verified ? "Zweryfikowany" : "Niezweryfikowany"}</td>
                          <td>{entry.tag_count ?? "—"}</td>
                          <td>{formatDateTime(entry.verified_at)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </ResponsiveTable>
              </div>

              <h3 className="panel__title">Niezarejestrowane</h3>
              <div className="table-wrap">
                <ResponsiveTable className="tbl">
                  <thead>
                    <tr>
                      <th>Kolczyk</th>
                      <th>Źródło</th>
                    </tr>
                  </thead>
                  <tbody>
                    {notFoundEntries.length === 0 ? (
                      <tr className="tbl__empty-row">
                        <td colSpan={2} className="muted">
                          Brak — wszystkie zeskanowane numery były na liście.
                        </td>
                      </tr>
                    ) : (
                      notFoundEntries.map((entry) => (
                        <tr key={entry.id}>
                          <td>
                            <EarTag value={entry.tag} />
                          </td>
                          <td>{entry.source || "—"}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </ResponsiveTable>
              </div>
            </>
          ) : null}
        </section>
      ) : null}

      {deleteOpen ? (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          data-testid="tags-history-delete-modal"
        >
          <form className="modal" onSubmit={onDelete}>
            <h3 className="modal__title">Usunąć sesję z historii?</h3>
            <p className="modal__text">Tej operacji nie da się cofnąć.</p>
            <label className="field">
              <span className="field__label">Hasło administratora</span>
              <input
                className="input"
                type="password"
                data-testid="tags-history-delete-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
                {...passwordFieldProps(`${fieldNs}-history-delete-password`)}
              />
            </label>
            <div className="modal__actions">
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => {
                  setDeleteOpen(false);
                  setPassword("");
                }}
              >
                Anuluj
              </button>
              <button
                type="submit"
                className="btn btn--danger"
                data-testid="tags-history-delete-confirm"
                disabled={busy}
              >
                Usuń
              </button>
            </div>
          </form>
        </div>
      ) : null}
    </main>
  );
}
